import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { exportNominationsToWbd } from '../lib/api';

/**
 * Administration → Export to WBD (NOC-396). A single button that downloads a
 * CSV of all live entries in the exact column format the World Buildings
 * Directory's WordPress wp-all-import expects. Super-admin only (gated in the
 * router and nav). No password — the file carries no emails/phone numbers.
 */
export function ExportWbd() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<string | null>(null);

  const handleExport = async () => {
    setDownloading(true);
    setError(null);
    try {
      const filename = await exportNominationsToWbd();
      setLastFile(filename);
    } catch (err) {
      console.error(err);
      setError('The export failed. Please try again, or contact support if it keeps happening.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack spacing={3} alignItems="center">
      <Typography variant="h3" component="h1" align="center">
        Export to WBD
      </Typography>

      <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 720, width: '100%' }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            sx={(t) => ({
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: t.palette.mode === 'dark' ? 'rgba(255,204,0,0.16)' : 'rgba(255,204,0,0.22)',
            })}
          >
            <PublicOutlinedIcon fontSize="large" sx={{ color: 'text.primary' }} />
          </Box>

          <Stack spacing={1}>
            <Typography variant="h6">Download the WBD import file</Typography>
            <Typography variant="body2" color="text.secondary">
              This downloads a .csv of all entries in the exact column format the World Buildings
              Directory website expects, ready to feed straight into its wp-all-import. Each row
              includes public links to the entry's images and A2 boards. If you would like any
              columns changing please contact dominic.cotterill@nocrus.co.uk
            </Typography>
          </Stack>

          <Alert severity="info" sx={{ width: '100%', textAlign: 'left' }}>
            Image links point at the live portal, so run the WordPress import while these entries
            and their images remain available on the admin portal.
          </Alert>

          <Button
            variant="contained"
            size="large"
            onClick={handleExport}
            disabled={downloading}
            startIcon={
              downloading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
          >
            {downloading ? 'Preparing…' : 'Export to WBD'}
          </Button>

          {lastFile && !error && (
            <Alert severity="success" sx={{ width: '100%' }}>
              Downloaded <strong>{lastFile}</strong>. Check your browser's downloads.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
